import { useEffect, useState } from "react";
import { Plus, Users, X } from "lucide-react";
import { Modal } from "./Modal";
import { request } from "./FunctionalApp";

async function api(path: string, init: RequestInit = {}) {
  const r = await request(path, init);
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(String(body.error || "Unable to save").replaceAll("_", " "));
  return body;
}
const post = (path: string, body: any) =>
  api(path, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
const put = (path: string, body: any) =>
  api(path, { method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });

export function AutonomousGroupSettings() {
  const [data, setData] = useState<any>({ groups: [], permissions: [], users: [] });
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [groupMembers, setGroupMembers] = useState<Record<string, string[]>>({});
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    const [groupData, userData] = await Promise.all([
      api("/api/admin/groups"),
      api("/api/admin/users"),
    ]);
    // AM groups = MAINTENANCE groups that have maintenance.autonomous.manage
    const amGroups = groupData.groups.filter((g: any) =>
      g.product === "MAINTENANCE" && (g.permissions.includes("maintenance.autonomous.complete") || g.permissions.includes("maintenance.autonomous.manage"))
    );
    // Maintenance users only
    const maintenanceUsers = userData.users.filter((u: any) =>
      u.active && (u.products || "").includes("MAINTENANCE")
    );
    setData({ groups: amGroups, allGroups: groupData.groups, permissions: groupData.permissions, users: maintenanceUsers });
    // Build member map from group_ids on users
    const members: Record<string, string[]> = {};
    for (const g of amGroups) members[g.id] = [];
    for (const u of maintenanceUsers) {
      const ids = String(u.group_ids || "").split(",").filter(Boolean);
      for (const gid of ids) if (members[gid] !== undefined) members[gid].push(u.id);
    }
    setGroupMembers(members);
  }

  useEffect(() => { load(); }, []);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      // Create the group
      const { id: groupId } = await post("/api/admin/groups", { name: newName, product: "MAINTENANCE" });
      // Auto-assign autonomous.complete and helpdesk permissions — not autonomous.manage (that's for engineers/managers)
      await put(`/api/admin/groups/${groupId}/permissions`, { permissions: ["maintenance.autonomous.complete", "maintenance.helpdesk.manage"] });
      setNewName(""); setCreating(false);
      setMessage(`Group "${newName}" created and assigned Autonomous Maintenance permission.`);
      await load();
      setExpanded(groupId);
    } catch (err: any) { setMessage(err.message); }
    finally { setBusy(false); }
  }

  async function toggleMember(groupId: string, userId: string, inGroup: boolean) {
    // Get all current group memberships for this user
    const user = data.users.find((u: any) => u.id === userId);
    const currentGroupIds = String(user?.group_ids || "").split(",").filter(Boolean);
    const nextGroupIds = inGroup
      ? currentGroupIds.filter((id: string) => id !== groupId)
      : [...currentGroupIds, groupId];
    await put(`/api/admin/users/${userId}/groups`, { groupIds: nextGroupIds });
    await load();
  }

  const amPermKey = "maintenance.autonomous.manage";

  return (
    <section className="func-panel" style={{ marginBottom: 16 }}>
      <div className="func-table-head">
        <div>
          <h2>Autonomous Maintenance department groups</h2>
          <p style={{ margin: "3px 0 0", fontSize: 12, color: "var(--muted)" }}>
            Department team leaders in these groups can complete autonomous checks and raise helpdesk tickets without engineer permissions.
          </p>
        </div>
        <button onClick={() => { setCreating(!creating); setMessage(""); }}>
          <Plus /> New department group
        </button>
      </div>

      {message ? <div className="func-admin-note">{message}</div> : null}

      {creating && (
        <Modal title="New department group" onClose={() => setCreating(false)}>
          <form onSubmit={create}>
            <label>
              Department name
              <input
                required autoFocus minLength={2} maxLength={100}
                placeholder="e.g. Packaging, Production, Warehouse"
                value={newName}
                onChange={e => setNewName(e.target.value)}
              />
            </label>
            <small>The group will automatically receive <b>autonomous.complete</b> and <b>helpdesk.manage</b> permissions — members complete checks and raise tickets without engineering access.</small>
            <button disabled={busy}>{busy ? "Creating…" : "Create group"}</button>
          </form>
        </Modal>
      )}

      {data.groups.length === 0 && !creating ? (
        <p className="func-empty">No autonomous department groups yet. Create one to assign team leaders to checks.</p>
      ) : null}

      {data.groups.map((group: any) => {
        const members = (groupMembers[group.id] || [])
          .map((id: string) => data.users.find((u: any) => u.id === id))
          .filter(Boolean);
        const nonMembers = data.users.filter((u: any) =>
          !(groupMembers[group.id] || []).includes(u.id)
        );
        const isExpanded = expanded === group.id;
        return (
          <div key={group.id} className="autonomous-group-card">
            <div className="autonomous-group-header" onClick={() => setExpanded(isExpanded ? null : group.id)}>
              <span className="asset-toggle">{isExpanded ? "▼" : "▶"}</span>
              <span className="autonomous-group-name">
                <b>{group.name}</b>
                <small>{members.length} member{members.length !== 1 ? "s" : ""} · Autonomous Maintenance only</small>
              </span>
              <Users size={16} style={{ color: "var(--muted)", marginLeft: "auto" }} />
            </div>
            {isExpanded && (
              <div className="autonomous-group-body">
                <div className="autonomous-group-members">
                  <h4>Current members</h4>
                  {members.length === 0 ? (
                    <p className="func-empty" style={{ padding: "10px 0" }}>No members yet.</p>
                  ) : (
                    members.map((u: any) => (
                      <div key={u.id} className="autonomous-member-row">
                        <span>
                          <b>{u.name}</b>
                          <small>{u.email}</small>
                        </span>
                        <button
                          className="secondary"
                          onClick={() => toggleMember(group.id, u.id, true)}
                        >
                          <X size={13} /> Remove
                        </button>
                      </div>
                    ))
                  )}
                </div>
                {nonMembers.length > 0 && (
                  <div className="autonomous-group-add">
                    <h4>Add member</h4>
                    <p style={{ fontSize: 12, color: "var(--muted)", margin: "0 0 10px" }}>
                      Only users with a Maintenance product seat are shown.
                    </p>
                    <div className="autonomous-add-list">
                      {nonMembers.map((u: any) => (
                        <div key={u.id} className="autonomous-member-row">
                          <span>
                            <b>{u.name}</b>
                            <small>{u.email} · {u.groups || "No other groups"}</small>
                          </span>
                          <button onClick={() => toggleMember(group.id, u.id, false)}>
                            <Plus size={13} /> Add
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}
